import React from 'react';
import $ from 'jquery';
import { connect } from "react-redux";
import { useState } from 'react';
/**
 * Checks whether the argument is an object
 * @param {any} o
 */
function isObject(o) {
  return o && !Array.isArray(o) && Object(o) === o;
}

/**
 * Checks whether constraints are valid
 * @param {MediaStreamConstraints} mediaType
 */
function validateMediaTrackConstraints(mediaType) {
  let supportedMediaConstraints = null;

  if (navigator.mediaDevices) {
    supportedMediaConstraints = navigator.mediaDevices.getSupportedConstraints();
  }

  if (supportedMediaConstraints === null) {
    return;
  }

  let unSupportedMediaConstraints = Object.keys(mediaType).filter(
    (constraint) => !supportedMediaConstraints[constraint]
  );

  if (unSupportedMediaConstraints.length !== 0) {
    let toText = unSupportedMediaConstraints.join(',');
    console.error(
      `The following constraints ${toText} are not supported on this browser.`
    );
  }
}

const noop = () => { };

/**
 *
 * @callback Callback
 * @param {Blob} blob
 *
 * @callback ErrorCallback
 * @param {Error} error
 *
 * @typedef MediaRecorderProps
 * @type {object}
 * @property {BlobPropertyBag} blobOptions
 * @property {boolean} recordScreen
 * @property {function} onStart
 * @property {Callback} onStop
 * @property {Callback} onDataAvailable
 * @property {ErrorCallback} onError
 * @property {object} mediaRecorderOptions
 * @property {MediaStreamConstraints} mediaStreamConstraints
 *
 * @typedef MediaRecorderHookOptions
 * @type {object}
 * @property {Error} error
 * @property {string} status
 * @property {Blob} mediaBlob
 * @property {boolean} isAudioMuted
 * @property {function} stopRecording,
 * @property {function} getMediaStream,
 * @property {function} clearMediaStream,
 * @property {function} startRecording,
 * @property {function} pauseRecording,
 * @property {function} resumeRecording,
 * @property {function} muteAudio
 * @property {function} unMuteAudio
 * @property {MediaStream} liveStream
 *
 * @param {MediaRecorderProps}
 * @returns {MediaRecorderHookOptions}
 */
function useMediaRecorder({
  blobOptions,
  recordScreen,
  onStop = noop,
  onStart = noop,
  onError = noop,
  onDataAvailable = noop,
  mediaRecorderOptions,
  mediaStreamConstraints = {}
}) {
  let mediaChunks = React.useRef([]);
  let mediaStream = React.useRef(null);
  let mediaRecorder = React.useRef(null);
  let [error, setError] = React.useState(null);
  let [status, setStatus] = React.useState('idle');
  let [mediaBlob, setMediaBlob] = React.useState(null);
  let [isAudioMuted, setIsAudioMuted] = React.useState(false);

  async function getMediaStream() {
    if (error) {
      setError(null);
    }

    setStatus('acquiring_media');

    try {
      let stream;

      if (recordScreen) {
        stream = await window.navigator.mediaDevices.getDisplayMedia(
          mediaStreamConstraints
        );
      } else {
        stream = await window.navigator.mediaDevices.getUserMedia(
          mediaStreamConstraints
        );
      }

      if (recordScreen && mediaStreamConstraints.audio) {
        let audioStream = await window.navigator.mediaDevices.getUserMedia({
          audio: mediaStreamConstraints.audio
        });

        audioStream
          .getAudioTracks()
          .forEach((audioTrack) => stream.addTrack(audioTrack));
      }

      mediaStream.current = stream;
      setStatus('ready');
    } catch (err) {
      setError(err);
      setStatus('failed');
    }
  }

  function clearMediaStream() {
    if (mediaRecorder.current) {
      mediaRecorder.current.removeEventListener(
        'dataavailable',
        handleDataAvailable
      );
      mediaRecorder.current.removeEventListener('stop', handleStop);
      mediaRecorder.current.removeEventListener('error', handleError);
      mediaRecorder.current = null;
    }

    if (mediaStream.current) {
      mediaStream.current.getTracks().forEach((track) => track.stop());
      mediaStream.current = null;
      mediaChunks.current = [];
    }
  }

  async function startRecording() {
    if (error) {
      setError(null);
    }

    if (!mediaStream.current) {
      await getMediaStream();
    }

    mediaChunks.current = [];

    if (mediaStream.current) {
      mediaRecorder.current = new MediaRecorder(
        mediaStream.current,
        mediaRecorderOptions
      );
      mediaRecorder.current.addEventListener(
        'dataavailable',
        handleDataAvailable
      );
      mediaRecorder.current.addEventListener('stop', handleStop);
      mediaRecorder.current.addEventListener('error', handleError);
      mediaRecorder.current.start();
      setStatus('recording');
      onStart();
    }
  }

  function handleDataAvailable(e) {
    if (e.data.size) {
      mediaChunks.current.push(e.data);
    }
    onDataAvailable(e.data);
  }

  function handleStop() {
    let [sampleChunk] = mediaChunks.current;
    let blobPropertyBag = Object.assign(
      { type: sampleChunk.type },
      blobOptions
    );
    let blob = new Blob(mediaChunks.current, blobPropertyBag);

    setStatus('stopped');
    setMediaBlob(blob);
    onStop(blob);
  }

  function handleError(e) {
    setError(e.error);
    setStatus('idle');
    onError(e.error);
  }

  function muteAudio(mute) {
    setIsAudioMuted(mute);

    if (mediaStream.current) {
      mediaStream.current.getAudioTracks().forEach((audioTrack) => {
        audioTrack.enabled = !mute;
      });
    }
  }

  function pauseRecording() {
    if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
      setStatus('paused');
      mediaRecorder.current.pause();
    }
  }

  function resumeRecording() {
    if (mediaRecorder.current && mediaRecorder.current.state === 'paused') {
      mediaRecorder.current.resume();
      setStatus('recording');
    }
  }

  function stopRecording() {
    if (mediaRecorder.current) {
      setStatus('stopping');
      mediaRecorder.current.stop();
      // not sure whether to place clean up in useEffect?
      // If placed in useEffect the handler functions become dependencies of useEffect
      mediaRecorder.current.removeEventListener(
        'dataavailable',
        handleDataAvailable
      );
      mediaRecorder.current.removeEventListener('stop', handleStop);
      mediaRecorder.current.removeEventListener('error', handleError);
      mediaRecorder.current = null;
      clearMediaStream();
    }
  }

  React.useEffect(() => {
    if (!window.MediaRecorder) {
      throw new ReferenceError(
        'MediaRecorder is not supported in this browser. Please ensure that you are running the latest version of chrome/firefox/edge.'
      );
    }

    if (recordScreen && !window.navigator.mediaDevices.getDisplayMedia) {
      throw new ReferenceError(
        'This browser does not support screen capturing'
      );
    }

    if (isObject(mediaStreamConstraints.video)) {
      validateMediaTrackConstraints(mediaStreamConstraints.video);
    }

    if (isObject(mediaStreamConstraints.audio)) {
      validateMediaTrackConstraints(mediaStreamConstraints.audio);
    }

    if (mediaRecorderOptions && mediaRecorderOptions.mimeType) {
      if (!MediaRecorder.isTypeSupported(mediaRecorderOptions.mimeType)) {
        console.error(
          `The specified MIME type supplied to MediaRecorder is not supported by this browser.`
        );
      }
    }
  }, [mediaStreamConstraints, mediaRecorderOptions, recordScreen]);

  return {
    error,
    status,
    mediaBlob,
    isAudioMuted,
    stopRecording,
    getMediaStream,
    startRecording,
    pauseRecording,
    resumeRecording,
    clearMediaStream,
    muteAudio: () => muteAudio(true),
    unMuteAudio: () => muteAudio(false),
    get liveStream() {
      if (mediaStream.current) {
        return new MediaStream(mediaStream.current.getVideoTracks());
      }
      return null;
    }
  };
}

/**
 * @typedef LiveStreamPreviewProps
 * @type {object}
 * @property {MediaStream} stream
 *
 * @param {LiveStreamPreviewProps}
 */
function LiveStreamPreview({ stream }) {
  let videoPreviewRef = React.useRef();

  React.useEffect(() => {
    if (videoPreviewRef.current && stream) {
      videoPreviewRef.current.srcObject = stream;
    }
  }, [stream]);

  if (!stream) {
    return null;
  }

  return <video ref={videoPreviewRef} width={520} height={480} autoPlay />;
}

/**
 * @typedef PlayerProps
 * @type {object}
 * @property {Blob} srcBlob
 *
 * @param {PlayerProps}
 */
function Player({ srcBlob }) {
  if (!srcBlob) {
    return null;
  }

  return (
    <div>
      <video
        src={URL.createObjectURL(srcBlob)}
        width={520}
        height={480}
        controls
        style={{ marginBottom: 50 }}
      />
      <button style={{ width: '10%', marginLeft: -350 }} onClick={() => {
        console.log('after stop blob:', srcBlob);
        var data = new FormData();
        data.append('video', srcBlob);  //Correct: sending the Blob itself
        data.append('student_id', 1438);
        data.append('teacher_id', 14);
        data.append('client_id', 2);

        $.ajax({
          type: "POST",
          enctype: 'multipart/form-data',
          url: "http://127.0.0.1:8000/api/upload/recording",
          data: data,
          processData: false,
          contentType: false,
          cache: false,
          timeout: 600000,
          success: function (data) {
            console.log(data);


          },
          error: function (e) {
            console.log("ERROR : ", e);
          }
        });
      }}>Done</button>
    </div>
  );
}



function VideoFeedback(props) {
  const [programNameState, setprogramNameState] = useState();
  const [programId, setprogramId] = useState("");
  const [courseId, setcourseId] = useState("");
  const [tempStudent, settempStudent] = useState([]);
  const [studentsList, setstudentsList] = useState([]);
  let [recordScreen, setRecordScreen] = React.useState(false);
  let [audio, setAudio] = React.useState(true);
  let {
    status,
    liveStream,
    mediaBlob,
    pauseRecording,
    resumeRecording,
    stopRecording,
    getMediaStream,
    startRecording,
    clearMediaStream
  } = useMediaRecorder({
    blobOptions: { type: 'video/mp4' },
    recordScreen,
    mediaStreamConstraints: { audio, video: true }
  });

  //eslint-disable-next-line
  React.useEffect(() => clearMediaStream, []);

  if (props.programNameState.programName !== programNameState) {
    const { programNameState } = props;
    setprogramNameState(programNameState.programName);
    setprogramId(programNameState.program);
    setcourseId(programNameState.courseId);
    debugger;
    var tutorData = JSON.parse(localStorage.getItem("loginData"));
    if (tutorData != null) {
      setstudentsList(tutorData.teacher_students);
      tutorData.teacher_students[programNameState.programName].map(item => {
        tempStudent.push({
          id: item.id,
          selected: false,
          user_name: item.user_name,
          image: item.image
        })
      })
    }
  }

  const selectUser = (e , id) => {
    debugger;
    tempStudent.map((item, index) => {
      if (item.id == id && item.selected == false) {
        debugger;
        item.selected = true
      }
      else {
        debugger;
        item.selected = false
      }
    })
    settempStudent([...tempStudent]);
  }

  return (
    <article>
      <div className="row">
        <div className="col-md-12">
          <div className="card shadow-sm p-3">
            <h5>Select a student to record video:</h5>
            <hr />
            <div className="row">
              {tempStudent != null && typeof tempStudent != "undefined" ? tempStudent.map((item, index) => (
                <div className="col-md-3 text-center">
                  <input type="checkbox" checked={item.selected} value={item.selected} key={index} className="mr-2" onChange={(e) => selectUser(e , item.id)} />
                  {item.image !== null ? <img src={`https://lincolnmethod.s3.us-east-1.amazonaws.com/profileImages/${item.image}`} alt="Avatar" className="avatarStudent" /> : <img src={require("../../../public/images/emptyuserphoto.png")} alt="Avatar" className="avatarStudent" />}
                  <br />
                  <label>{item.user_name}</label>
                </div>
              )) : null}

              <div className="col-md-12">
                {
                  status == "failed" ?
                    <label> "Video can not be recorded untill app can not access to camera, please allow app to access camera" </label> :
                    <label>Status: <span className="font-weight-bold"> {status}</span> </label>
                }
              </div>
            </div>
            <dialog open={status === 'acquiring_media'}>
              Waiting for permissions
            </dialog>
            {/* <p>
        Select video source
        <label>
          <input
            type="radio"
            checked={recordScreen}
            onChange={() => setRecordScreen((prevState) => !prevState)}
          />{' '}
          Screen
        </label>
        <label>
          <input
            type="radio"
            checked={!recordScreen}
            onChange={() => setRecordScreen((prevState) => !prevState)}
          />{' '}
          Camera
        </label>
      </p> */}

            {/* <p>
              <label>
                Enable microphone
          <input
                  type="checkbox"
                  checked={audio}
                  onChange={() => setAudio((prevState) => !prevState)}
                />
              </label>
            </p> */}

            <section className="p-3">
              {tempStudent.find(i => i.selected == true) && status !== 'recording' && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={async () => {
                    await getMediaStream();
                    startRecording();
                  }}
                >
                  Start recording
                </button>
              )}
              {status === 'recording' && (
                <button className="btn btn-primary"
                  type="button" onClick={pauseRecording}>
                  Pause recording
                </button>
              )}
              {status === 'paused' && (
                <button className="btn btn-primary"
                  type="button" onClick={resumeRecording}>
                  Resume recording
                </button>
              )}
              {status === 'recording' && (
                <button className="btn btn-primary"
                  type="button" onClick={stopRecording}>
                  Stop recording
                </button>
              )}
            </section>
            <LiveStreamPreview stream={liveStream} />
            <Player srcBlob={mediaBlob} />
          </div>
        </div>
      </div>
    </article>
  );
}
function mapStateToProps(state) {
  return {
    programNameState: state.feedTutorRed.programName,
    classStartTime: state.feedTutorRed.classStartTime
  };
}
export default connect(mapStateToProps, {})(VideoFeedback);